import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Zap, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [needsTotp, setNeedsTotp] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) navigate(user.role === "bedriftsbruker" ? "/portal" : "/"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await login(email, password, totp || null);
      if (r.requires_totp) {
        setNeedsTotp(true);
        toast.info("Skriv inn 2FA-koden fra appen din");
      } else {
        toast.success(`Velkommen, ${r.user.full_name}`);
        navigate(r.user.role === "bedriftsbruker" ? "/portal" : "/");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Klarte ikke å logge inn";
      toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen w-full bg-[#050505] grid-bg relative overflow-hidden" data-testid="login-page">
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-emerald-500/[0.03] to-transparent" />
      <div className="relative min-h-screen flex">
        {/* Venstre: brand */}
        <div className="hidden lg:flex flex-col justify-between p-12 w-[45%] border-r border-zinc-900">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-md bg-gradient-to-br from-emerald-400 to-emerald-600 grid place-items-center">
              <Zap className="h-5 w-5 text-black" strokeWidth={2.5} />
            </div>
            <div className="font-heading text-xl font-semibold tracking-tight">Krafthub</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-emerald-400 font-medium mb-3">CIS · ERP · Elhub · Nord Pool</div>
            <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-50 leading-tight">
              Én driftsplattform<br />for norske kraftleverandører.
            </h2>
            <p className="text-zinc-400 mt-5 max-w-md leading-relaxed">
              Administrer kunder, målepunkter, avtaler, prisberegning og fakturering — sømløst integrert
              med Elhub, Nord Pool og norske sikkerhetsstandarder.
            </p>
          </div>
          <div className="flex items-center gap-6 text-xs text-zinc-500">
            <div className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 text-emerald-400" /> GDPR</div>
            <div>Kryptering (AES-256)</div>
            <div>2FA / TOTP</div>
            <div>Audit-logg</div>
          </div>
        </div>

        {/* Høyre: form */}
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            <div className="lg:hidden mb-8 flex items-center gap-3">
              <div className="h-9 w-9 rounded-md bg-gradient-to-br from-emerald-400 to-emerald-600 grid place-items-center">
                <Zap className="h-5 w-5 text-black" strokeWidth={2.5} />
              </div>
              <div className="font-heading text-xl font-semibold tracking-tight">Krafthub</div>
            </div>

            <div className="mb-8">
              <h1 className="font-heading text-3xl font-semibold tracking-tight text-zinc-50">Logg inn</h1>
              <p className="text-sm text-zinc-400 mt-1">Tilgang til Krafthub-plattformen</p>
            </div>

            <form onSubmit={submit} className="space-y-4" data-testid="login-form">
              <div>
                <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1.5">E-post</label>
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-11 px-3.5 rounded-md bg-zinc-900/70 border border-zinc-800 text-zinc-100 focus:outline-none focus:border-emerald-500/60 transition-colors"
                  autoComplete="email" required data-testid="login-email"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1.5">Passord</label>
                <input
                  type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-11 px-3.5 rounded-md bg-zinc-900/70 border border-zinc-800 text-zinc-100 focus:outline-none focus:border-emerald-500/60 transition-colors"
                  autoComplete="current-password" required data-testid="login-password"
                />
              </div>
              {needsTotp && (
                <div>
                  <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1.5">2FA-kode</label>
                  <input
                    value={totp} onChange={(e) => setTotp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    inputMode="numeric" placeholder="000000"
                    className="w-full h-11 px-3.5 rounded-md bg-zinc-900/70 border border-zinc-800 text-zinc-100 tracking-widest text-center focus:outline-none focus:border-emerald-500/60"
                    data-testid="login-totp"
                  />
                </div>
              )}
              <button
                type="submit" disabled={loading}
                className="w-full h-11 rounded-md bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-black font-medium transition-colors flex items-center justify-center gap-2"
                data-testid="login-submit"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Logg inn
              </button>
            </form>

            <div className="mt-8 pt-6 border-t border-zinc-900 text-[11px] text-zinc-600 leading-relaxed">
              Trenger du tilgang? Kontakt administrator i selskapet ditt.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
